import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Golf Bundles (EPIC #116 P1) — lists the reseller's BUNDLE groupings
 * (GroupingType='bundles'), separate from Stay & Play destinations. Each
 * grouping is the same machinery (settings/radius/courses/branding/embed) but
 * holds prepaid-round bundles. Opening one routes to the shared grouping
 * editor, which shows a Bundles tab.
 */
export default function GolfBundlesList() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [groupings, setGroupings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showNew, setShowNew] = useState(false)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/groupings', { params: { type: 'bundles' } })
      .then(r => setGroupings(r.data?.groupings || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  return (
    <ResellerShell title={t('sp_nav_golf_bundles', 'Golf Bundles')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 24 }}>
        <p style={{ color: '#64748b', maxWidth: 640, marginTop: 0, marginBottom: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_bundle_groups_intro', 'A bundle group is a collection of prepaid-round bundles (e.g. 4 rounds for $325, 8 for $550) with its own settings, courses, branding, and embed. Create a group, then build bundles inside it.')}
        </p>
        <button onClick={() => setShowNew(true)} style={{ ...primaryBtn, whiteSpace: 'nowrap' }}>
          {t('sp_bundle_groups_new', '+ New bundle group')}
        </button>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <div style={{ marginBottom: 24, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
        {t('sp_bundle_tax_note', 'Tax is added automatically at checkout using your configured tax rates.')}
      </div>

      {loading
        ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
        : groupings.length === 0
          ? <div style={empty}>{t('sp_bundle_groups_none', 'No bundle groups yet. Click "+ New bundle group" to create your first.')}</div>
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
              {groupings.map(g => (
                <Link key={g.ID_NO} to={`/portal/reseller/groupings/${g.ID_NO}`} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 16, color: '#0f172a' }}>{g.Name}</strong>
                    <StatusBadge value={g.Status} />
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                    {g.CenterLabel || `${g.CenterLatitude}, ${g.CenterLongitude}`} · {t('sp_radius_km_suffix', '{km}km radius', { km: g.RadiusKm })}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                    {t('sp_bundle_groups_count', '{count} bundles', { count: g.BundleCount || 0 })}
                  </div>
                </Link>
              ))}
            </div>
          )}

      {showNew && <NewBundleGroupModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/portal/reseller/groupings/${id}`)} />}
    </ResellerShell>
  )
}

function NewBundleGroupModal({ onClose, onCreated }) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      // Default center = middle of Canada; the editor's Settings tab lets them
      // place the real map pin + radius. type='bundles' keeps it out of the
      // Stay & Play list.
      const { data } = await tournamentApi.post('/portal/reseller/groupings', {
        type: 'bundles', name,
        centerLatitude: 56.130366, centerLongitude: -106.346771,
        centerLabel: '(set on next screen)', radiusKm: 50,
      })
      onCreated(data.groupingId)
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{t('sp_bundle_groups_new_title', 'New bundle group')}</h3>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_name_label', 'Name')}</label>
          <input autoFocus required type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder={t('sp_bundle_groups_name_ph', 'Cape Breton Golf Passes, etc.')} style={inp} />
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>{t('sp_set_center_next_screen', "You'll set the center location and radius on the next screen.")}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
            <button type="submit" disabled={submitting || !name.trim()} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? t('creating', 'Creating…') : t('sp_create_edit', 'Create + edit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StatusBadge({ value }) {
  const { t } = useLanguage()
  const colors = {
    draft: { bg: '#fef3c7', fg: '#92400e' }, active: { bg: '#dcfce7', fg: '#166534' },
    paused: { bg: '#e2e8f0', fg: '#475569' }, archived: { bg: '#f1f5f9', fg: '#94a3b8' },
  }
  const c = colors[value] || colors.draft
  return <span style={{ padding: '2px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{t('sp_status_' + value, value)}</span>
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18, textDecoration: 'none', color: 'inherit', display: 'block' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, cursor: 'pointer' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 480 }
